'use client';

import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { Button, Text } from '@/ui';
import { cn } from '@lib';
import { GeometryService } from '@lib/server';
import { IconCurrentLocation, IconGridDots, IconMap, IconSearch } from '@tabler/icons-react';
import { Command as CommandPrimitive } from 'cmdk';
import { debounce } from 'lodash';
import { toast } from 'sonner';

import type { PlaceSearchPlace, SearchResults } from '.';
import { ActionBarWrapper } from '../action-bar-wrapper';
import { ToggleGroup, ToggleGroupItem } from '../toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip';
import { sortPlaces } from './sort-places';

export const PlacesSearchBar = ({
  view,
  onViewChange,
  places,
  searchResults,
  setSortedPlaces,
  setSearchLocation,
  setSearchResults,
  setOverrideViewState,
  handlePinClick,
  accessToken,
}: {
  view: 'grid' | 'map';
  setSearchLocation: Dispatch<SetStateAction<number[] | null>>;
  onViewChange: (view: 'grid' | 'map') => void;
  searchResults: SearchResults[];
  setOverrideViewState: Dispatch<SetStateAction<any>>;
  places: PlaceSearchPlace[];
  setSortedPlaces: Dispatch<SetStateAction<PlaceSearchPlace[]>>;
  setSearchResults: Dispatch<SetStateAction<SearchResults[]>>;
  handlePinClick: (lat: number, long: number) => void;
  accessToken: string;
} & React.ComponentProps<'input'>) => {
  const geometryService = new GeometryService(accessToken);

  const [open, setOpen] = useState(false);
  const [location, setLocation] = useState<number[] | null>(null);
  const [value, setValue] = useState('');
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);
  const [, setHasNavigator] = useState(false);

  useEffect(() => {
    setHasNavigator('geolocation' in navigator);
  }, []);

  const handleFocus = () => {
    if (blurTimeout.current) {
      clearTimeout(blurTimeout.current);
    }
    setOpen(true);
  };

  const handleBlur = () => {
    blurTimeout.current = setTimeout(() => {
      setOpen(false);
    }, 100);
  };

  const handleSearchInputChange = (e: string) => {
    setValue(e);

    const search = debounce(async () => {
      if (!open) setOpen(true);

      if (e) {
        const result = await geometryService.forwardGeocode(e, { limit: 5 });
        setSearchResults(result?.features);
      } else {
        setSearchResults([]);
      }
    }, 200);

    search();
  };

  const getMyLocation = () => {
    if (location) {
      const sortedPlaces = sortPlaces(location[0], location[1], places);

      setSearchLocation([location[0], location[1]]);
      setSortedPlaces(sortedPlaces);
      handlePinClick(sortedPlaces[0].geo.latitude, sortedPlaces[0].geo.longitude);
      return;
    }
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
    } else {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const sortedPlaces = sortPlaces(latitude, longitude, places);

          setLocation([latitude, longitude]);
          setSearchLocation([latitude, longitude]);
          setSortedPlaces(sortedPlaces);
          handlePinClick(sortedPlaces[0].geo.latitude, sortedPlaces[0].geo.longitude);
        },
        () => {
          setHasNavigator(false);
          toast.error('Unable to retrieve your location');
        },
      );
    }
  };

  return (
    <CommandPrimitive
      shouldFilter={false}
      loop
      className="absolute -top-28 z-20 w-full max-w-[calc(100vw-2rem)] place-self-center sm:max-w-(--breakpoint-sm)"
    >
      <CommandPrimitive.List
        className={cn(
          'bg-popover border-border absolute z-0 flex w-full flex-col overflow-hidden rounded-lg border p-1 shadow-lg transition-all duration-300 ease-out',
          { 'max-h-[3.875rem]': !open },
          { 'max-h-none pt-[7.125rem] sm:pt-[4.25rem]': open },
        )}
      >
        <CommandPrimitive.Empty>
          <Text element="span" className="text-muted-foreground line-clamp-1 flex h-9 items-center px-3">
            No results found.
          </Text>
        </CommandPrimitive.Empty>

        {searchResults?.map((result) => (
          <CommandPrimitive.Item
            onSelect={() => {
              setSortedPlaces(
                sortPlaces(Number(result.geometry.coordinates[1]), Number(result.geometry.coordinates[0]), places),
              );
              setSearchLocation([Number(result.geometry.coordinates[1]), Number(result.geometry.coordinates[0])]);
              setSearchResults([]);
              setOpen(false);
              setOverrideViewState({
                latitude: Number(result.geometry.coordinates[1]),
                longitude: Number(result.geometry.coordinates[0]),
                zoom: 14,
              });
            }}
            value={result.place_name}
            key={result.place_name}
            className="aria-selected:bg-accent aria-selected:text-accent-foreground line-clamp-1 flex overflow-hidden"
            asChild
          >
            <Button variant={'ghost'} className="flex w-full justify-start">
              <span className="flex w-0 grow truncate">{result.place_name}</span>
            </Button>
          </CommandPrimitive.Item>
        ))}
      </CommandPrimitive.List>

      <ActionBarWrapper className="bg-card focus-within:ring-primary/50 ring-offset-background relative z-30 flex-col items-stretch gap-3 p-2.5 ring-offset-1 transition ease-out focus-within:ring-2 sm:flex-row sm:items-center">
        <TooltipProvider>
          <div className="relative flex grow items-center">
            <CommandPrimitive.Input
              value={value}
              onValueChange={handleSearchInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="Search for places"
              className="peer placeholder:text-muted-foreground text-card-foreground w-full self-stretch bg-transparent pl-9 text-sm focus:outline-hidden"
            />

            <IconSearch
              size={20}
              className="peer-focus:text-card-foreground text-muted-foreground pointer-events-none absolute left-1.5 transition"
            />

            <Tooltip>
              <TooltipContent>Use My Location</TooltipContent>

              <TooltipTrigger asChild>
                <Button onClick={getMyLocation} className={'shrink-0'} variant={'outline'} size={'icon'}>
                  <IconCurrentLocation size={20} />
                </Button>
              </TooltipTrigger>
            </Tooltip>
          </div>

          <ToggleGroup type="single" value={view} onValueChange={onViewChange}>
            {[
              { value: 'grid', label: 'Grid View', icon: IconGridDots },
              { value: 'map', label: 'Map View', icon: IconMap },
            ].map(({ icon: Icon, label, value }) => (
              <Tooltip key={label}>
                <TooltipContent>{label}</TooltipContent>

                <TooltipTrigger asChild>
                  <div className="w-full">
                    <ToggleGroupItem className="h-10 w-full gap-3 sm:w-10" value={value}>
                      <Icon />
                      <span className="flex sm:hidden">{label}</span>
                    </ToggleGroupItem>
                  </div>
                </TooltipTrigger>
              </Tooltip>
            ))}
          </ToggleGroup>
        </TooltipProvider>
      </ActionBarWrapper>
    </CommandPrimitive>
  );
};
